/**
 * Custom layout events used to inform interested apps of layout changes
 */
export const LAYOUT_EVENTS = {
    /**
     * Custom event to show the cart (so far only on JobDetails page). Only the JobDetail app should be listening
     * to this event
     */
    CART_SHOWN: 'cart-shown',
    /**
     * Custom event to let the GlobalElements app know that the cart has been
     * hidden. This is so that the LayoutProvider can update the layout grid
     */
    CART_HIDDEN: 'cart-hidden',
    /**
     * Custom event to expand the Summary Cart. Only the Summary Cart should be
     * listening to this event
     */
    EXPAND_CART: 'expand-summary-cart',
    /**
     * Custom event to collapse the Summary Cart. Only the Summary Cart should be
     * listening to this event
     */
    COLLAPSE_CART: 'collapse-summary-cart',
    /**
     * Custom event to collapse the Sidebar. Only the Sidebar app should be
     * listening to this event
     */
    COLLAPSE_SIDEBAR: 'collapse-sidebar',

    /**
     * Custom event to expand the Sidebar. Only the Sidebar app should be
     * listening to this event
     */
    EXPAND_SIDEBAR: 'expand-sidebar',
    /**
     * Custom event to let the GlobalElements app know that the Sidebar has been collapsed
     */
    SIDEBAR_COLLAPSED: 'sidebar-collapsed',
    /**
     * Custom event to let the GlobalElements app know that the Sidebar has been expanded
     */
    SIDEBAR_EXPANDED: 'sidebar-expanded',

    /**
     * Custom event dispatched by the Root app once login, feature flags,
     * and root config have all been initialised. Listening apps should
     * gate any page-load network requests behind this event to ensure
     * user data and feature flags are available.
     */
    APP_READY: 'app-ready',
} as const;

/**
 * Sets the global app-ready flag so that late-mounting micro-frontends
 * can synchronously check whether APP_READY has already been dispatched.
 * This should be called immediately before dispatching the APP_READY event.
 */
export const setAppReady = () => {
    window.__APP_READY__ = true;
};

/**
 * Returns `true` if the APP_READY event has already been dispatched.
 * Use this to handle the race condition where a component mounts after
 * the event has fired.
 */
export const isAppReady = (): boolean => {
    return window.__APP_READY__ === true;
};

// This is needed to let TypeScript know about the custom event we're dispatching.
// Without this, any code that tries to listen for this event will not have the
// correct typings for the event object.
declare global {
    // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
    interface DocumentEventMap {
        [LAYOUT_EVENTS.APP_READY]: CustomEvent;
    }
    // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
    interface Window {
        __APP_READY__?: boolean;
    }
}

export const JOB_DETAIL_STATUS_FILTERS = {
    ACTION_REQUIRED: 'actionRequired',
    EXCLUDED: 'excluded',
    ORDERED: 'ordered',
    DRAFT_ORDER: 'orderDrafted',
    QUOTING: 'quoting',
    READY_TO_ORDER: 'readyToOrder',
    READY_TO_ORDER_QUOTED: 'readyToOrderQuoted',
    RECEIVED: 'received',
    REQUEST_MORE_QUOTES: 'requestingMoreQuotes',
    STATUS: 'status',
    REVIEW_COMPLIANCE: 'reviewCompliance',
} as const;

export const POPOVER_DELAY = 300;
export const MOUSE_STOP = 'mousestop';

export const ERROR_GRAPHIC_ALTERNATE_TEXT_REBRAND =
    'Orderly mechanic holding steering wheel';
